/**
 * Admin nav contract (STORY-008-006). Each item names the RBAC permission
 * that reveals it (slugs verbatim from apps/api/garden/auth/permissions.py —
 * do NOT invent). Icons are lucide-react components imported directly (the
 * @garden/ui Icon map lacks these names; see NotFound.tsx for the
 * direct-import precedent).
 *
 * SCRUM-152: 15 items (Field library, Need mappings, Changesets, Seller
 * suggestions, Bulk import, Knowledge base, Prompts, Evals, Price references,
 * Feature flags, App config, AI spend, Broadcast, Out-of-catalogue, Feedback)
 * were hidden from the sidebar per the reporter's annotated screenshot. Their
 * routes/pages/permissions are untouched — only the nav entry is gone, so
 * they're one line each to restore if this scope was too broad.
 *
 * isAdminPermission() = "holds ANY admin-prefixed permission" — the admit
 * predicate for RequireAdminAuth (six top-level RBAC namespaces).
 */
import {
  LayoutDashboard,
  Store,
  Package,
  FolderTree,
  MapPin,
  ClipboardCheck,
  ClipboardList,
  Users,
  UserPlus,
  Scroll,
  Radio,
  SlidersHorizontal,
  type LucideIcon,
} from "lucide-react";

export interface AdminNavItem {
  id: string;
  to: string;
  labelKey: string;
  icon: LucideIcon;
  requiredPermission: string;
}

export interface AdminNavGroup {
  id: string;
  labelKey: string;
  items: readonly AdminNavItem[];
}

export const ADMIN_NAV: readonly AdminNavGroup[] = [
  {
    id: "dashboard",
    labelKey: "nav.group.dashboard",
    items: [
      { id: "dashboard", to: "/admin/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, requiredPermission: "ops.metrics.read" },
    ],
  },
  {
    id: "marketplace",
    labelKey: "nav.group.marketplace",
    items: [
      { id: "sellers", to: "/admin/sellers", labelKey: "nav.sellers", icon: Store, requiredPermission: "marketplace.seller.read" },
      { id: "products", to: "/admin/products", labelKey: "nav.products", icon: Package, requiredPermission: "marketplace.product.read" },
      { id: "categories", to: "/admin/categories", labelKey: "nav.categories", icon: FolderTree, requiredPermission: "marketplace.category.manage" },
      { id: "markets", to: "/admin/markets", labelKey: "nav.markets", icon: MapPin, requiredPermission: "marketplace.market.manage" },
      { id: "seller-applications", to: "/admin/seller-applications", labelKey: "nav.sellerApplications", icon: ClipboardCheck, requiredPermission: "marketplace.seller.approve" },
      { id: "product-review", to: "/admin/product-review", labelKey: "nav.productReview", icon: ClipboardList, requiredPermission: "marketplace.product.approve" },
    ],
  },
  {
    id: "operations",
    labelKey: "nav.group.operations",
    items: [
      { id: "users", to: "/admin/users", labelKey: "nav.users", icon: Users, requiredPermission: "users.read" },
      { id: "live-sessions", to: "/admin/live-sessions", labelKey: "nav.liveSessions", icon: Radio, requiredPermission: "ops.metrics.read" },
      { id: "invitations", to: "/admin/invitations", labelKey: "nav.invitations", icon: UserPlus, requiredPermission: "users.invite_admin" },
      { id: "audit-log", to: "/admin/audit-log", labelKey: "nav.auditLog", icon: Scroll, requiredPermission: "ops.audit.read" },
      // App config was one of the 15 entries SCRUM-152 hid from the sidebar.
      // Restored deliberately and ALONE: the six chat rate-limit windows became
      // admin-editable here (fbde3c0), so this page went from "rarely needed" to
      // the place an operator unblocks a throttled tester. Without a nav entry
      // that knob is reachable only by someone who already knows the URL, which
      // is how a 429 turns into a support ticket. The other 14 removals stand.
      { id: "app-config", to: "/admin/app-config", labelKey: "nav.appConfig", icon: SlidersHorizontal, requiredPermission: "ops.app_config.read" },
    ],
  },
] as const;

export const ADMIN_PERMISSION_PREFIXES: readonly string[] = [
  "users.",
  "marketplace.",
  "content.",
  "ops.",
  "ooc.",
  "feedback.",
] as const;

export function isAdminPermission(slug: string): boolean {
  return ADMIN_PERMISSION_PREFIXES.some((p) => slug.startsWith(p));
}
